import { Option, Schema } from 'effect';
import type { RuntimeErrorCode, StructuredOutputErrorCode } from './types';

// ── Error-code vocabulary ───────────────────────────────────────────────────
// The codes travel over the wire (server error bodies, CLI machine envelopes,
// SDK throws), so `Schema` owns the vocabulary and every membership test decodes
// through it. The accepted sets are byte-identical to the union types in
// `./types`; the codecs are the single runtime mirror of those unions.

export const structuredOutputErrorCodeCodec = Schema.Literals([
  'SCHEMA_VALIDATION_FAILED',
  'EXTRACTION_FAILED',
  'TIMEOUT',
  'DOCUMENT_NOT_FOUND',
] as const);

export const runtimeErrorCodeCodec = Schema.Literals([
  'SCHEMA_VALIDATION_FAILED',
  'EXTRACTION_FAILED',
  'TIMEOUT',
  'DOCUMENT_NOT_FOUND',
  'INVALID_REQUEST',
  'UNAUTHORIZED',
  'HTTP_ERROR',
  'NOT_FOUND',
  'NOT_IMPLEMENTED',
  'INVALID_RESPONSE',
] as const);

const decodeStructuredOutputErrorCode = Schema.decodeUnknownOption(structuredOutputErrorCodeCodec);
const decodeRuntimeErrorCode = Schema.decodeUnknownOption(runtimeErrorCodeCodec);

/** True when an unknown value is one of the structured-output error codes. */
export function isStructuredOutputErrorCode(value: unknown): value is StructuredOutputErrorCode {
  return Option.isSome(decodeStructuredOutputErrorCode(value));
}

/** True when an unknown value is one of the SDK runtime error codes. */
export function isRuntimeErrorCode(value: unknown): value is RuntimeErrorCode {
  return Option.isSome(decodeRuntimeErrorCode(value));
}

export class OkraRuntimeError extends Error {
  readonly code: RuntimeErrorCode;
  readonly status: number;
  readonly details?: unknown;

  constructor(code: RuntimeErrorCode, message: string, status = 500, details?: unknown) {
    super(message);
    this.name = 'OkraRuntimeError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export class StructuredOutputError extends OkraRuntimeError {
  readonly code: StructuredOutputErrorCode;

  constructor(code: StructuredOutputErrorCode, message: string, status: number, details?: unknown) {
    super(code, message, status, details);
    this.name = 'StructuredOutputError';
    this.code = code;
  }
}
